import { defineStore } from 'pinia'

import type { Brand, LabelBriefPair } from '@/types/brand'
import type { Logo } from '@/types/logo'

interface BrandState {
  storedBrands: Brand[]
  brands: Brand[]
  selectedBrand: Brand | null
  selectedBrandId: number | null
  logos: Logo[]
  baseColours: string[]
  colours: string[]
  selectedLogo: Logo | null
  selectedColor: string
  baseLogo: Logo | null
  baseColor: string
  currentPageNumber: number
  totalBrandsCount: number
  addedBrandsCount: number
  filters: any
  labelsAndBriefs: LabelBriefPair[]
  activeLabel: LabelBriefPair
  selectedBackgroundColorType: number
  selectedTextColorType: number
}

export const useBrandStore = defineStore('brand', {
  state: (): BrandState => ({
    storedBrands: [],
    brands: [],
    selectedBrand: null,
    selectedBrandId: null,
    logos: [],
    baseColours: [],
    colours: [],
    selectedLogo: null,
    selectedColor: '',
    baseLogo: null,
    baseColor: '',
    currentPageNumber: 1,
    addedBrandsCount: 0,
    totalBrandsCount: -1,
    filters: {},
    labelsAndBriefs: [],
    activeLabel: { label: '', brief: '' },
    selectedBackgroundColorType: 0,
    selectedTextColorType: 0
  }),
  actions: {
    setBrands(brands: Brand[]): void {
      this.brands = brands
      this.storedBrands = JSON.parse(JSON.stringify(brands))
    },
    setSelectedBrand(selectedBrand: Brand | null): void {
      this.selectedBrand = selectedBrand
      this.selectedBrandId = selectedBrand?.id ?? null
      this.logos = selectedBrand ? JSON.parse(JSON.stringify(selectedBrand.logoUrls)) : []
      this.colours = selectedBrand ? JSON.parse(JSON.stringify(selectedBrand?.colours)) : []
      this.selectedLogo = this.logos[0] ?? null
    },
    updateLogo(index: number, logo: Logo): void {
      this.logos[index] = logo
      this.selectedBrand!.logoUrls[index] = logo
    },
    setSelectedLogo(logo: Logo | null): void {
      this.selectedLogo = logo
    },
    setSelectedColor(color: string): void {
      this.selectedColor = color
      if (!this.colours.includes(color)) {
        this.colours.unshift(color)
      }
    },
    setBaseData(logo: Logo | null, color: string, backgroundColors: string[]) {
      this.baseColor = color
      this.baseColours = [color, ...backgroundColors, ...this.colours]
      this.baseLogo = logo
      this.selectedLogo = logo
      for (const bgColor of backgroundColors) {
        if (!this.colours.includes(bgColor) && bgColor.toLowerCase() !== '#ffffff' && bgColor.toLowerCase() != '#000000') {
          this.colours.splice(0, 0, bgColor)
        }
      }
      this.setSelectedColor(color)
    },
    setLabelsBriefs(labelsAndBriefs: LabelBriefPair[]) {
      this.labelsAndBriefs = labelsAndBriefs
    },
    setActiveLabel(label: LabelBriefPair): void {
      this.activeLabel = label
    },
    resetBrandState(): void {
      this.brands = this.brands.splice(0, this.addedBrandsCount)
      this.selectedBrand = null
      this.selectedBrandId = null
      this.selectedLogo = null
      this.selectedColor = ''
      this.baseLogo = null
      this.baseColor = ''
      this.currentPageNumber = 0
      this.totalBrandsCount = -1
      this.filters = {}
      this.labelsAndBriefs = []
      this.activeLabel = { label: '', brief: '' }
      this.selectedBackgroundColorType = 0
      this.selectedTextColorType = 0
    },
    setFilter(filter: any) {
      this.filters = { ...this.filters, ...filter }
    },
    clearFilter() {
      this.filters = {}
    },
    appendBrands(brands: Brand[]) {
      this.brands.push(...brands)
    },
    prependBrand(brand: Brand) {
      this.brands.splice(0, 0, brand)
    },
    clearBrands() {
      this.brands = []
      this.currentPageNumber = 0
      this.totalBrandsCount = -1
    },
    clearLabelsAndSelection() {
      this.labelsAndBriefs = []
      this.activeLabel = { label: '', brief: '' }
    },
  },
})
